import {
  browserSessionPersistence,
  GoogleAuthProvider,
  TotpMultiFactorGenerator,
  getIdTokenResult,
  getMultiFactorResolver,
  multiFactor,
  onAuthStateChanged,
  reauthenticateWithPopup,
  reload,
  sendEmailVerification,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type TotpSecret,
  type User,
  type UserCredential,
} from 'firebase/auth'
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
  type WriteBatch,
} from 'firebase/firestore'
import type { Client } from '../types'
import { allowedEmail, getFirebaseServices } from './firebase'
import {
  isDemoClient,
  persistentClients,
  prepareClientForPersistence,
} from './clientPersistence'

const BATCH_LIMIT = 400

export interface TotpChallenge {
  resolver: MultiFactorResolver
  factors: MultiFactorInfo[]
}

export type SignInResult =
  | { kind: 'signed-in'; user: User }
  | { kind: 'totp-required'; challenge: TotpChallenge }

export interface TotpEnrollment {
  secret: TotpSecret
  secretKey: string
  authenticatorUri: string
}

function clientsPath(userId: string) {
  const { firestore } = getFirebaseServices()
  return collection(firestore, 'users', userId, 'clients')
}

function isClient(value: DocumentData): value is Client {
  return (
    typeof value.id === 'string' &&
    typeof value.company === 'string' &&
    typeof value.updatedAt === 'string' &&
    Array.isArray(value.interactions)
  )
}

function getErrorCode(reason: unknown): string {
  return typeof reason === 'object' && reason !== null && 'code' in reason
    ? String(reason.code)
    : ''
}

function assertAllowedUser(user: User): void {
  if (
    user.email?.toLocaleLowerCase('en-US') !== allowedEmail ||
    !user.emailVerified
  ) {
    throw new Error(`Доступ разрешён только для подтверждённого аккаунта ${allowedEmail}.`)
  }
}

async function runPrimarySignIn(
  operation: () => Promise<UserCredential>,
): Promise<SignInResult> {
  const { auth } = getFirebaseServices()
  try {
    const result = await operation()
    return { kind: 'signed-in', user: result.user }
  } catch (reason) {
    if (getErrorCode(reason) !== 'auth/multi-factor-auth-required') throw reason

    const resolver = getMultiFactorResolver(auth, reason as MultiFactorError)
    const factors = resolver.hints.filter(
      (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    )
    if (!factors.length) {
      throw new Error('Для аккаунта подключён неподдерживаемый второй фактор.')
    }
    return { kind: 'totp-required', challenge: { resolver, factors } }
  }
}

export function observeUser(callback: (user: User | null) => void): Unsubscribe {
  const { auth } = getFirebaseServices()
  return onAuthStateChanged(auth, callback)
}

export async function signInWithGoogle(): Promise<SignInResult> {
  const { auth } = getFirebaseServices()
  await setPersistence(auth, browserSessionPersistence)
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  const result = await runPrimarySignIn(() => signInWithPopup(auth, provider))
  if (result.kind === 'signed-in') {
    try {
      assertAllowedUser(result.user)
    } catch (reason) {
      await signOut(auth)
      throw reason
    }
  }
  return result
}

export function hasPasswordProvider(user: User): boolean {
  return user.providerData.some((provider) => provider.providerId === 'password')
}

export function hasTotpProvider(user: User): boolean {
  return multiFactor(user).enrolledFactors.some(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  )
}

export async function signInWithPassword(password: string): Promise<SignInResult> {
  const { auth } = getFirebaseServices()
  await setPersistence(auth, browserSessionPersistence)
  const result = await runPrimarySignIn(() =>
    signInWithEmailAndPassword(auth, allowedEmail, password),
  )

  if (result.kind === 'totp-required') return result

  if (!result.user.emailVerified) {
    try {
      await sendEmailVerification(result.user)
    } catch (reason) {
      if (getErrorCode(reason) !== 'auth/too-many-requests') {
        await signOut(auth)
        throw reason
      }
    }
    await signOut(auth)
    throw new Error(
      `Подтвердите адрес ${allowedEmail}. Письмо со ссылкой отправлено на почту.`,
    )
  }

  assertAllowedUser(result.user)
  return result
}

export async function completeTotpSignIn(
  challenge: TotpChallenge,
  factorUid: string,
  verificationCode: string,
): Promise<User> {
  const factor = challenge.factors.find((item) => item.uid === factorUid)
  if (!factor || factor.factorId !== TotpMultiFactorGenerator.FACTOR_ID) {
    throw new Error('Второй фактор изменился. Начните вход заново.')
  }

  const code = verificationCode.replace(/\s+/g, '')
  if (!/^\d{6}$/.test(code)) throw new Error('Введите шестизначный код.')

  const assertion = TotpMultiFactorGenerator.assertionForSignIn(factor.uid, code)
  const result = await challenge.resolver.resolveSignIn(assertion)
  try {
    assertAllowedUser(result.user)
  } catch (reason) {
    await signOut(getFirebaseServices().auth)
    throw reason
  }
  return result.user
}

export async function linkPasswordToCurrentUser(password: string): Promise<User> {
  const { auth } = getFirebaseServices()
  const user = auth.currentUser
  if (!user) throw new Error('Сначала войдите через Google.')

  await reload(user)
  assertAllowedUser(user)

  const googleProfile = user.providerData.find(
    (provider) => provider.providerId === GoogleAuthProvider.PROVIDER_ID,
  )
  if (googleProfile?.email?.toLocaleLowerCase('en-US') !== allowedEmail) {
    throw new Error('Для добавления пароля требуется подтверждённый Google-аккаунт.')
  }

  try {
    await updatePassword(user, password)
  } catch (reason) {
    if (getErrorCode(reason) !== 'auth/requires-recent-login') throw reason

    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    await reauthenticateWithPopup(user, provider)
    await updatePassword(user, password)
  }

  await reload(user)
  await getIdTokenResult(user, true)
  assertAllowedUser(user)
  return user
}

export async function beginTotpEnrollment(): Promise<TotpEnrollment> {
  const { auth } = getFirebaseServices()
  const user = auth.currentUser
  if (!user) throw new Error('Сначала войдите в аккаунт.')

  await reload(user)
  assertAllowedUser(user)
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  await reauthenticateWithPopup(user, provider)
  assertAllowedUser(user)

  const session = await multiFactor(user).getSession()
  const secret = await TotpMultiFactorGenerator.generateSecret(session)
  return {
    secret,
    secretKey: secret.secretKey,
    authenticatorUri: secret.generateQrCodeUrl(user.email ?? allowedEmail, 'WorkSmarter'),
  }
}

export async function completeTotpEnrollment(
  enrollment: TotpEnrollment,
  verificationCode: string,
): Promise<User> {
  const { auth } = getFirebaseServices()
  const user = auth.currentUser
  if (!user) throw new Error('Сессия истекла. Войдите заново.')

  const code = verificationCode.replace(/\s+/g, '')
  if (!/^\d{6}$/.test(code)) throw new Error('Введите шестизначный код.')

  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    enrollment.secret,
    code,
  )
  await multiFactor(user).enroll(assertion, 'WorkSmarter Authenticator')

  // Enrollment is already committed on the server. A transient refresh failure
  // must not make the UI claim that enrollment failed or expose the secret again.
  try {
    await reload(user)
    await getIdTokenResult(user, true)
  } catch {
    // The next sign-in will refresh the session and require the new factor.
  }
  return user
}

export async function signOutUser(): Promise<void> {
  const { auth } = getFirebaseServices()
  await signOut(auth)
}

export function watchClients(
  userId: string,
  onClients: (clients: Client[], fromCache: boolean) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    clientsPath(userId),
    { includeMetadataChanges: true },
    (snapshot) => {
      const allClients = snapshot.docs
        .map((item) => item.data())
        .filter(isClient)
      const demoClients = allClients.filter(isDemoClient)

      if (demoClients.length && !snapshot.metadata.fromCache) {
        void Promise.all(
          demoClients.map((client) =>
            deleteDoc(doc(getFirebaseServices().firestore, 'users', userId, 'clients', client.id)),
          ),
        ).catch(() => undefined)
      }

      onClients(
        allClients
          .filter((client) => !isDemoClient(client))
          .map(prepareClientForPersistence),
        snapshot.metadata.fromCache,
      )
    },
    onError,
  )
}

export async function saveCloudClient(userId: string, client: Client): Promise<void> {
  if (isDemoClient(client)) return
  const { firestore } = getFirebaseServices()
  const persistentClient = prepareClientForPersistence(client)
  await setDoc(doc(firestore, 'users', userId, 'clients', persistentClient.id), persistentClient)
}

export async function deleteCloudClient(userId: string, clientId: string): Promise<void> {
  const { firestore } = getFirebaseServices()
  await deleteDoc(doc(firestore, 'users', userId, 'clients', clientId))
}

async function commitInChunks(
  operations: Array<(batch: WriteBatch) => void>,
): Promise<void> {
  const { firestore } = getFirebaseServices()
  for (let index = 0; index < operations.length; index += BATCH_LIMIT) {
    const batch = writeBatch(firestore)
    operations.slice(index, index + BATCH_LIMIT).forEach((operation) => operation(batch))
    await batch.commit()
  }
}

export async function saveCloudClients(userId: string, clients: Client[]): Promise<void> {
  const { firestore } = getFirebaseServices()
  const clientsToSave = persistentClients(clients)
  await commitInChunks(
    clientsToSave.map((client) => (batch) => {
      batch.set(doc(firestore, 'users', userId, 'clients', client.id), client)
    }),
  )
}

export async function replaceCloudClients(
  userId: string,
  previousClients: Client[],
  nextClients: Client[],
): Promise<void> {
  const { firestore } = getFirebaseServices()
  const persistentNextClients = persistentClients(nextClients)
  const nextIds = new Set(persistentNextClients.map((client) => client.id))
  const operations: Array<(batch: WriteBatch) => void> = []

  previousClients
    .filter((client) => !nextIds.has(client.id))
    .forEach((client) => {
      operations.push((batch) => {
        batch.delete(doc(firestore, 'users', userId, 'clients', client.id))
      })
    })

  persistentNextClients.forEach((client) => {
    operations.push((batch) => {
      batch.set(doc(firestore, 'users', userId, 'clients', client.id), client)
    })
  })

  await commitInChunks(operations)
}
